import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { X, Loader2 } from 'lucide-react';
import { RichTextEditor } from '../RichTextEditor';
import { API_URL } from '../../services/api';

export function DepositInvoiceModal({ onClose, quote, grandTotal, onSubmit, loading }) {
  const [depositPercent, setDepositPercent] = useState(25);
  const [accountCode, setAccountCode] = useState('230');
  const [taxType, setTaxType] = useState('GST on Income');
  const [description, setDescription] = useState(
    quote ? `25% Deposit invoice for works on ${quote.quote_number}` : ''
  );

  const subTotal = (grandTotal * depositPercent) / 100;
  const gst = subTotal * 0.1;
  const amountDue = subTotal + gst;

  useEffect(() => {
    if (quote) {
      setDescription(`${depositPercent}% Deposit invoice for works on ${quote.quote_number}`);
    }
  }, [depositPercent, quote]);

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit({
      client_id: quote.client_id,
      property_address_id: quote.property_address_id,
      quote_id: quote.id,
      is_deposit: true,
      deposit_percent: depositPercent,
      descriptions: [{ 
        description: description, 
        amount: subTotal 
      }]
    });
  };

  const handlePreviewDeposit = async () => {
    try {
      const token = localStorage.getItem('token');
      const resp = await fetch(`${API_URL}/api/invoices/deposit-preview`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(token ? { 'Authorization': `Bearer ${token}` } : {}) },
        credentials: 'include',
        body: JSON.stringify({
          quote_id: quote.id,
          client_id: quote.client_id,
          property_address_id: quote.property_address_id,
          descriptions: [{ description, amount: subTotal }]
        })
      });
      if (!resp.ok) throw new Error('Preview failed');
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank');
    } catch (err) {
      toast.error('Failed to generate preview');
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="p-4 border-b flex justify-between items-center sticky top-0 bg-white z-10">
          <h2 className="font-semibold">Create Deposit Invoice</h2>
          <button onClick={onClose}><X size={20} /></button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="block text-xs font-medium mb-1">Deposit</label>
              <div className="flex items-center">
                <input 
                  type="number" 
                  value={depositPercent} 
                  onChange={(e) => setDepositPercent(parseFloat(e.target.value) || 0)}
                  className="w-full px-3 py-2 border border-slate-300 rounded-l text-sm text-right"
                  min="1" max="100"
                />
                <span className="px-3 py-2 bg-slate-100 border border-l-0 border-slate-300 rounded-r text-sm">%</span>
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium mb-1">Account Code</label>
              <input type="text" value={accountCode} onChange={(e) => setAccountCode(e.target.value)} className="w-full px-3 py-2 border border-slate-300 rounded text-sm" />
            </div>
            <div>
              <label className="block text-xs font-medium mb-1">Tax Type</label>
              <select value={taxType} onChange={(e) => setTaxType(e.target.value)} className="w-full px-3 py-2 border border-slate-300 rounded text-sm">
                <option value="GST on Income">GST on Income</option>
                <option value="GST Free">GST Free</option>
                <option value="No GST">No GST</option>
              </select>
            </div>
          </div>

          <div className="flex justify-end">
            <button type="button" onClick={handlePreviewDeposit} className="px-4 py-2 text-sm bg-yellow-500 text-white rounded" data-testid="deposit-preview-btn">Preview</button>
          </div>

          <div>
            <label className="block text-xs font-medium mb-1">Description</label>
            <RichTextEditor value={description} onChange={setDescription} />
          </div>

          <div className="border-t pt-4 space-y-2">
            <div className="flex justify-between text-sm">
              <span>Quote Total:</span>
              <span>${grandTotal.toFixed(2)}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span>Sub Total ({depositPercent}% of {grandTotal.toFixed(2)}):</span>
              <span>${subTotal.toFixed(2)}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span>Total GST 10%:</span>
              <span>${gst.toFixed(2)}</span>
            </div>
            <div className="flex justify-between text-lg font-bold border-t pt-2">
              <span>Amount Due AUD:</span>
              <span className="text-primary">${amountDue.toFixed(2)}</span>
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm border border-slate-300 rounded">Cancel</button>
            <button type="submit" disabled={loading} className="px-4 py-2 text-sm bg-blue-500 text-white rounded flex items-center gap-2">
              {loading && <Loader2 size={16} className="animate-spin" />}
              Create
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// Site Visit Schedule Modal
